import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Sparkles, Trophy, Ticket, Users, Clock, ChevronDown, ChevronUp, X, Target, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PALETTE = [
    '#FFD700', '#00FF88', '#FF007F', '#00E0FF', '#FF8C00',
    '#8A2BE2', '#FF3B30', '#4ADE80', '#F472B6', '#38BDF8',
    '#FBBF24', '#22D3EE',
];

/**
 * "Wheel the Names" — live player-dashboard widget.
 *
 *  · Polls /api/wheels/active every 15s (faster near end-time).
 *  · Renders a live SVG wheel with each entrant's name in weighted slices.
 *  · Auto-spins the moment the wheel transitions from active → settled
 *    (or when opened during the first minutes after firing).
 *  · Session-scoped dismissable — reappears next session or on new wheel.
 */
const WheelWidget = () => {
    const { user } = useAuth();
    const [data, setData] = useState({ active: null, recent_settled: null });
    const [expanded, setExpanded] = useState(true);
    const [spinning, setSpinning] = useState(false);
    const [angle, setAngle] = useState(0);
    const [announcedFor, setAnnouncedFor] = useState(null);
    const [dismissedIds, setDismissedIds] = useState(() => {
        try { return JSON.parse(sessionStorage.getItem('wheel_dismissed') || '[]'); } catch { return []; }
    });
    const prevActiveIdRef = useRef(null);

    const wheel = data.active || data.recent_settled;
    const isSettled = !data.active && !!data.recent_settled;

    const fetchNow = useCallback(async () => {
        try {
            const { data } = await axios.get(`${API}/wheels/active`, { withCredentials: true });
            setData(data || { active: null, recent_settled: null });
        } catch { /* silent */ }
    }, []);

    useEffect(() => {
        fetchNow();
        // Adaptive poll: faster near end-time
        let cancelled = false;
        const tick = async () => {
            if (cancelled) return;
            await fetchNow();
            const endsMs = wheel?.ends_at ? new Date(wheel.ends_at).getTime() : Infinity;
            const dt = endsMs - Date.now();
            const next = dt < 60_000 ? 3_000 : dt < 5 * 60_000 ? 8_000 : 15_000;
            setTimeout(tick, next);
        };
        const t = setTimeout(tick, 15_000);
        return () => { cancelled = true; clearTimeout(t); };
    }, []);

    // Trigger spin animation when active → settled
    useEffect(() => {
        const prevId = prevActiveIdRef.current;
        if (data.active) {
            prevActiveIdRef.current = data.active.id;
        } else if (data.recent_settled && prevId === data.recent_settled.id && data.recent_settled.status === 'settled') {
            // Just transitioned to settled during this session
            if (announcedFor !== data.recent_settled.id) {
                triggerSpin(data.recent_settled);
                setAnnouncedFor(data.recent_settled.id);
            }
            prevActiveIdRef.current = null;
        } else if (data.recent_settled && !prevId) {
            // Landed straight into settled (e.g. fresh mount right after fire)
            const firedAt = data.recent_settled.fired_at ? new Date(data.recent_settled.fired_at).getTime() : 0;
            if (Date.now() - firedAt < 3 * 60 * 1000 && announcedFor !== data.recent_settled.id) {
                triggerSpin(data.recent_settled);
                setAnnouncedFor(data.recent_settled.id);
            }
        }
    }, [data.active?.id, data.recent_settled?.id, data.recent_settled?.status]);

    // Live countdown text
    const [now, setNow] = useState(Date.now());
    useEffect(() => {
        const t = setInterval(() => setNow(Date.now()), 1000);
        return () => clearInterval(t);
    }, []);

    // Weighted slices — one slice per ticket (so bigger loads visibly bigger)
    const slices = useMemo(() => {
        const entries = wheel?.entries || [];
        const out = [];
        entries.forEach((e, ei) => {
            const tickets = Math.max(1, Number(e.tickets) || 1);
            for (let i = 0; i < tickets; i++) {
                out.push({ ...e, sliceKey: `${e.user_id}-${i}`, colorIdx: ei });
            }
        });
        return out;
    }, [wheel?.entries]);

    const triggerSpin = (w) => {
        if (!w?.winner_user_id) return;
        const list = [];
        (w.entries || []).forEach((e, ei) => {
            const tickets = Math.max(1, Number(e.tickets) || 1);
            for (let i = 0; i < tickets; i++) list.push({ user_id: e.user_id, colorIdx: ei });
        });
        if (list.length === 0) return;
        const winIdx = list.findIndex(s => s.user_id === w.winner_user_id);
        if (winIdx < 0) return;
        const step = 360 / list.length;
        // Land the winner's slice center precisely under the top pointer.
        // Slice i's initial center sits at `(i * step + step/2 - 90)°` in the
        // SVG coord system (slices are drawn from -π/2, sweeping clockwise).
        // The pointer is at 12 o'clock (SVG angle -90° / 270°). So we rotate
        // by `-(i * step + step/2)` (mod 360) — i.e. `360 - i*step - step/2`
        // — plus 8 full turns of visual drama.
        const target = 360 * 8 + (360 - (winIdx * step + step / 2));
        setSpinning(true);
        setAngle(target);
        setTimeout(() => {
            setSpinning(false);
            if (w.winner_name) {
                try { toast.success(`🎉 ${w.winner_name} won $${w.prize_amount} on ${w.name}!`, { duration: 6000 }); } catch { /* silent */ }
            }
        }, 6200);
    };

    if (!wheel) return null;
    if (dismissedIds.includes(wheel.id)) return null;
    // 2026-02-26: hide the settled wheel from the lobby ~30 minutes after
    // it fires. Keeps the "just fired" hype visible for a bit after the
    // spin, then declutters the dashboard automatically. Active/scheduled
    // wheels are unaffected — they render right up until they fire.
    if (isSettled && wheel.fired_at) {
        const firedMs = new Date(wheel.fired_at).getTime();
        if (Number.isFinite(firedMs) && Date.now() - firedMs > 30 * 60 * 1000) {
            return null;
        }
    }

    const endsMs = new Date(wheel.ends_at).getTime();
    const startsMs = new Date(wheel.starts_at).getTime();
    const remaining = wheel.status === 'scheduled' ? Math.max(0, startsMs - now) : Math.max(0, endsMs - now);
    const mm = Math.floor(remaining / 60000);
    const ss = Math.floor((remaining % 60000) / 1000);

    const dismiss = () => {
        const next = [...dismissedIds, wheel.id];
        setDismissedIds(next);
        try { sessionStorage.setItem('wheel_dismissed', JSON.stringify(next)); } catch { /* silent */ }
    };

    return (
        <div
            className="bg-gradient-to-br from-[#1a0f2e] via-[#0d0620] to-[#1a0f2e] border border-[#FFD700]/40 rounded-2xl p-4 relative overflow-hidden"
            data-testid="wheel-widget"
        >
            {/* Sparkle decoration */}
            <div className="absolute -top-6 -right-6 opacity-10 pointer-events-none">
                <Sparkles className="w-32 h-32 text-[#FFD700]" />
            </div>

            <div className="flex items-start justify-between gap-2 relative">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <Sparkles className="w-5 h-5 text-[#FFD700]" />
                        <span className="text-lg font-bold text-white truncate">{wheel.name}</span>
                        <span
                            className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#FFD700]/20 text-[#FFD700] border border-[#FFD700]/40"
                            data-testid="wheel-status-pill"
                        >
                            {isSettled ? 'JUST FIRED' : wheel.status}
                        </span>
                    </div>
                    <div className="text-xs text-[#c8b8e8] mt-1">
                        Prize <b className="text-[#FFD700]">${wheel.prize_amount}</b> ·
                        1 entry per <b className="text-white">${wheel.min_load}</b> loaded
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => setExpanded(v => !v)}
                        className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                        aria-label={expanded ? 'Hide wheel' : 'Show wheel'}
                        data-testid="wheel-toggle-btn"
                    >
                        {expanded ? <ChevronUp className="w-4 h-4 text-white" /> : <ChevronDown className="w-4 h-4 text-white" />}
                    </button>
                    <button
                        onClick={dismiss}
                        className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                        aria-label="Dismiss"
                        data-testid="wheel-dismiss-btn"
                    >
                        <X className="w-4 h-4 text-white/70" />
                    </button>
                </div>
            </div>

            {expanded && (
                <div className="mt-3 grid md:grid-cols-2 gap-4 items-center">
                    {/* Wheel SVG */}
                    <div className="relative flex justify-center">
                        <WheelSVG slices={slices} angle={angle} spinning={spinning} settled={isSettled} data-testid="wheel-svg" />
                    </div>

                    {/* Stats + entrant list */}
                    <div className="space-y-3">
                        <div className="grid grid-cols-3 gap-2">
                            <Stat icon={Users} label="Players" value={wheel.unique_players} />
                            <Stat icon={Ticket} label="Entries" value={wheel.total_tickets} />
                            <Stat icon={Ticket} label="You" value={wheel.my_tickets} highlight={wheel.my_tickets > 0} />
                        </div>

                        {!isSettled && (
                            <div className="bg-black/40 border border-white/10 rounded-xl p-3 text-center">
                                <div className="text-xs uppercase tracking-wider text-[#c8b8e8]">
                                    {wheel.status === 'scheduled' ? 'Starts in' : 'Fires in'}
                                </div>
                                <div className="text-3xl font-mono font-bold text-white mt-1" data-testid="wheel-countdown">
                                    {String(mm).padStart(2, '0')}:{String(ss).padStart(2, '0')}
                                </div>
                                <div className="text-[10px] text-[#8891A0] mt-1 flex items-center justify-center gap-1">
                                    <Clock className="w-3 h-3" />
                                    Ends {new Date(wheel.ends_at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} CT
                                </div>
                            </div>
                        )}

                        {isSettled && (
                            <div className="bg-[#FFD700]/10 border border-[#FFD700]/60 rounded-xl p-3 text-center" data-testid="wheel-winner-card">
                                <Trophy className="w-6 h-6 mx-auto text-[#FFD700]" />
                                <div className="text-xs uppercase tracking-wider text-[#FFD700] mt-1">Winner</div>
                                <div className="text-lg font-bold text-white mt-1">
                                    {wheel.winner_name || 'No entrants'}
                                </div>
                                {wheel.winner_name && (
                                    <>
                                        <div className="text-sm text-[#c8b8e8] mt-1">
                                            won <b className="text-[#FFD700]">${wheel.prize_amount}</b>
                                        </div>
                                        <div className="text-[10px] text-[#c8b8e8] mt-1 italic">
                                            Credited as bonus · 1× play-through then cashable
                                        </div>
                                    </>
                                )}
                                {/* Bridge to the moved WinnersWall — deep-links
                                    into Profile so late players can still see
                                    who won recently without clutter on lobby. */}
                                <a
                                    href="/profile#wheel-history"
                                    className="mt-2 inline-flex items-center gap-1 text-[11px] font-black tracking-wide text-[#FFD700] hover:text-[#fff2a0] underline underline-offset-2"
                                    data-testid="wheel-view-history-link"
                                >
                                    View wheel history →
                                </a>
                            </div>
                        )}

                        {wheel.my_tickets === 0 && !isSettled && (
                            <BullseyeCTA
                                minLoad={wheel.min_load}
                                prize={wheel.prize_amount}
                                user={user}
                            />
                        )}
                        {wheel.my_tickets > 0 && !isSettled && (
                            <div className="text-xs text-center text-[#00FF88] font-semibold flex items-center justify-center gap-1">
                                <Zap className="w-3 h-3" /> Auto-load ${wheel.min_load}+ again to stack more entries.
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const Stat = ({ icon: Icon, label, value, highlight }) => (
    <div className={`rounded-xl border p-2 text-center ${highlight ? 'border-[#FFD700] bg-[#FFD700]/10' : 'border-white/10 bg-black/40'}`}>
        <Icon className={`w-4 h-4 mx-auto ${highlight ? 'text-[#FFD700]' : 'text-[#c8b8e8]'}`} />
        <div className="text-lg font-bold text-white mt-0.5">{value ?? 0}</div>
        <div className="text-[10px] uppercase tracking-wider text-[#8891A0]">{label}</div>
    </div>
);

/**
 * Bullseye/target CTA — concentric rings that pulse toward the center, with
 * the prize dollar amount in the bullseye. Tapping it routes to the automated
 * PayPal "Buy Credits" flow so the user goes from "I want in" → funded fast.
 */
const BullseyeCTA = ({ minLoad, prize, user }) => {
    const onTap = () => {
        // Route to the automated PayPal "Buy Credits" flow — credits land
        // instantly, then the player is one tap from entering the wheel.
        try { window.dispatchEvent(new CustomEvent('wheel:load-cta-tapped', { detail: { minLoad, prize } })); } catch { /* silent */ }
        window.location.assign('/buy-credits');
    };

    return (
        <button
            type="button"
            onClick={onTap}
            className="w-full group relative overflow-hidden rounded-2xl border-2 border-[#FFD700] bg-gradient-to-br from-[#FFD700]/20 via-[#FF8C00]/10 to-[#FF007F]/20 p-4 text-left transition-transform hover:scale-[1.02] active:scale-[0.98] focus:outline-none focus:ring-2 focus:ring-[#FFD700]/60"
            data-testid="wheel-bullseye-cta"
            aria-label={`Load $${minLoad} or more to enter the wheel`}
        >
            <div className="flex items-center gap-3">
                {/* Concentric target rings — pure CSS */}
                <div className="relative w-16 h-16 shrink-0" aria-hidden="true">
                    <div className="absolute inset-0 rounded-full bg-[#FF3B30] animate-pulse" />
                    <div className="absolute inset-1.5 rounded-full bg-white" />
                    <div className="absolute inset-3 rounded-full bg-[#FF3B30]" />
                    <div className="absolute inset-[18px] rounded-full bg-white" />
                    <div className="absolute inset-6 rounded-full bg-[#FFD700] flex items-center justify-center">
                        <Target className="w-3 h-3 text-black" strokeWidth={3} />
                    </div>
                    {/* Radar sweep */}
                    <div className="absolute inset-0 rounded-full border-2 border-[#FFD700]/60 animate-ping" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider text-[#FFD700] font-bold">
                        Aim for the prize
                    </div>
                    <div className="text-base font-bold text-white leading-tight mt-0.5">
                        Load <span className="text-[#FFD700]">${minLoad}</span> to enter →
                    </div>
                    <div className="text-[11px] text-[#c8b8e8] mt-0.5">
                        Win <b className="text-[#FFD700]">${prize}</b> · Every ${minLoad} auto-loaded via receipt = 1 entry
                    </div>
                </div>
                <Zap className="w-5 h-5 text-[#FFD700] shrink-0 group-hover:animate-pulse" />
            </div>
        </button>
    );
};



const WheelSVG = ({ slices, angle, spinning, settled }) => {
    const size = 220;
    const r = 100;
    const cx = size / 2;
    const cy = size / 2;
    const n = slices.length;

    if (n === 0) {
        return (
            <div className="w-[220px] h-[220px] rounded-full border-2 border-dashed border-white/20 flex items-center justify-center text-xs text-[#8891A0] text-center px-4">
                No entries yet — be the first!
            </div>
        );
    }

    const step = (2 * Math.PI) / n;
    // Idle drift: slow, enticing continuous rotation while the wheel is
    // still gathering entries (not spinning, not settled). Mirrors the
    // Roulette wheel's idle animation — one full turn every 60s.
    const idle = !spinning && !settled;

    return (
        <div className="relative" style={{ width: size, height: size }} data-testid="wheel-svg-container">
            {/* Pointer at top */}
            <div
                className="absolute left-1/2 -translate-x-1/2 -top-1 z-10"
                style={{ width: 0, height: 0, borderLeft: '10px solid transparent', borderRight: '10px solid transparent', borderTop: '18px solid #FFD700' }}
            />
            <motion.svg
                width={size}
                height={size}
                animate={{ rotate: spinning ? angle : idle ? angle + 360 : angle }}
                transition={
                    spinning
                        ? { duration: 6, ease: [0.17, 0.67, 0.24, 1] }
                        : idle
                            ? { duration: 60, ease: 'linear', repeat: Infinity, repeatType: 'loop' }
                            : { duration: 0 }
                }
                style={{ transformOrigin: '50% 50%' }}
            >
                {slices.map((s, i) => {
                    const a0 = i * step - Math.PI / 2;
                    const a1 = (i + 1) * step - Math.PI / 2;
                    const x0 = cx + r * Math.cos(a0);
                    const y0 = cy + r * Math.sin(a0);
                    const x1 = cx + r * Math.cos(a1);
                    const y1 = cy + r * Math.sin(a1);
                    const large = step > Math.PI ? 1 : 0;
                    const path = `M ${cx} ${cy} L ${x0} ${y0} A ${r} ${r} 0 ${large} 1 ${x1} ${y1} Z`;
                    const color = PALETTE[s.colorIdx % PALETTE.length];
                    // Label position
                    const labelA = (a0 + a1) / 2;
                    const lx = cx + (r * 0.65) * Math.cos(labelA);
                    const ly = cy + (r * 0.65) * Math.sin(labelA);
                    const rotDeg = (labelA * 180) / Math.PI;
                    const shortName = (s.name || 'Player').split(' ')[0].slice(0, 10);
                    return (
                        <g key={s.sliceKey}>
                            <path d={path} fill={color} stroke={s.is_me ? '#fff' : '#000'} strokeWidth={s.is_me ? 2 : 0.5} opacity={0.92} />
                            <text
                                x={lx}
                                y={ly}
                                fill="#0a0a0a"
                                fontSize={n > 20 ? 8 : n > 12 ? 10 : 12}
                                fontWeight="bold"
                                textAnchor="middle"
                                dominantBaseline="middle"
                                transform={`rotate(${rotDeg}, ${lx}, ${ly})`}
                                pointerEvents="none"
                            >
                                {shortName}
                            </text>
                        </g>
                    );
                })}
                {/* Center hub */}
                <circle cx={cx} cy={cy} r={12} fill="#0a0a0a" stroke="#FFD700" strokeWidth={2} />
            </motion.svg>
        </div>
    );
};

export default WheelWidget;
